import api from './api';

// type: 'PDR' | 'APV' | 'TRX'
const reportingFinancierApi = {
  // ─── Vue Global (shell) ───────────────────────────────────────────────
  // Récupère les KPIs principaux des 6 services en un seul appel.
  getGlobalOverview: (params, signal) =>
    api.get('/reporting-financier/global/overview', { params, signal }),

  // Union des concessions / dépts / marques sur les 3 services branchés.
  getGlobalDimensions: () =>
    api.get('/reporting-financier/global/dimensions'),

  // ─── Vue par segment (APV / PDR / TRX) ────────────────────────────────
  // KPIs globaux + ventilations (par concession, dept, marque, prestation APV)
  getKpis: (type, params, signal) =>
    api.get(`/reporting-financier/${type}/kpis`, { params, signal }),

  // Comparaison N / N-1 : Mois en cours / Mois N-1 / Cumul année / Cumul N-1
  getComparison: (type, params, signal) =>
    api.get(`/reporting-financier/${type}/comparison`, { params, signal }),

  // Série temporelle (granularité day/week/month)
  getTimeseries: (type, params, signal) =>
    api.get(`/reporting-financier/${type}/timeseries`, { params, signal }),

  // Valeurs distinctes pour les selects de filtre (par segment)
  getDimensions: (type) =>
    api.get(`/reporting-financier/${type}/dimensions`),

  // ─── Référentiel départements (normalisation, bouton « Référentiel » PDR) ──
  // Liste les codes bruts de `factures_services.department` + leur libellé courant.
  getPdrDepartements: () =>
    api.get('/reporting-financier/pdr/config/departements'),
  // items: [{ code_brut, libelle }] — libellé vide = suppression du mapping.
  savePdrDepartements: (items) =>
    api.put('/reporting-financier/pdr/config/departements', { items }),

  // ─── Vue Ventes (VN / VO) — source bases snapshot ventes_vn/ventes_vo ──
  // type: 'vn' | 'vo'. KPIs nets (volume/CA/marge), ventilation marque +
  // concession, évolution mensuelle.
  getVentesKpis: (type, params, signal) =>
    api.get(`/reporting-financier/ventes/${type}/kpis`, { params, signal }),
  // Comparaison Mois en cours / Cumul année vs N-1
  getVentesComparison: (type, params, signal) =>
    api.get(`/reporting-financier/ventes/${type}/comparison`, { params, signal }),
  // Marques distinctes (select de filtre)
  getVentesDimensions: (type) =>
    api.get(`/reporting-financier/ventes/${type}/dimensions`),

  // ─── Stock VO — rapport « stock à date » (base snapshot stock_vo_detail) ──
  // La période n'est pas utilisée (photo à date) ; seuls concession + marque.
  getStockVoKpis: (params, signal) =>
    api.get('/reporting-financier/stock/vo/kpis', { params, signal }),
  getStockVoDimensions: () =>
    api.get('/reporting-financier/stock/vo/dimensions'),

  // ─── Stock VN — idem sur la base snapshot stock_vn_detail ──
  getStockVnKpis: (params, signal) =>
    api.get('/reporting-financier/stock/vn/kpis', { params, signal }),
  getStockVnDimensions: () =>
    api.get('/reporting-financier/stock/vn/dimensions'),

  // ─── Marché — immatriculations (base snapshot Google Sheet gsheet_data_immat) ──
  // Parts de marché par marque / groupe / type de client, vue par département,
  // évolution mensuelle. Filtres : période (shell) + département + VP/VUL (local).
  getMarcheKpis: (params, signal) =>
    api.get('/reporting-financier/marche/kpis', { params, signal }),
  getMarcheDimensions: () =>
    api.get('/reporting-financier/marche/dimensions'),
  // Référentiel marché (super-admin) : marques (constructeur+origine), affectations groupe/concession datées
  getMarcheConfig: () =>
    api.get('/reporting-financier/marche/config'),
  saveMarcheMarques: (items) =>
    api.put('/reporting-financier/marche/config/marques', { items }),
  getMarcheAffectations: (marque, departement) =>
    api.get('/reporting-financier/marche/config/affectations', { params: { marque, departement } }),
  saveMarcheAffectations: (payload) =>
    api.put('/reporting-financier/marche/config/affectations', payload),
  bootstrapMarche: () =>
    api.post('/reporting-financier/marche/config/bootstrap', {}),

  // Lancer une analyse IA (ou récupérer la version en cache < 24h)
  runAnalyseIa: (type, body) =>
    api.post(`/reporting-financier/${type}/analyse-ia`, body),

  // Historique des analyses IA déjà lancées
  getAnalyseIaHistory: (type, limit = 20) =>
    api.get(`/reporting-financier/${type}/analyse-ia/history`, { params: { limit } }),

  // ─── Budget ───────────────────────────────────────────────────────────
  getBudgetLignes: (params) => api.get('/budget/lignes', { params }),
  getBudgetDimensions: () => api.get('/budget/dimensions'),
  updateBudgetLigne: (id, montant) => api.put(`/budget/lignes/${id}`, { montant }),
  updateBudgetBulk: (updates) => api.put('/budget/lignes/bulk', { updates }),
  importBudget: (formData) => api.post('/budget/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportBudget: (annee) => api.get('/budget/export', { params: { annee }, responseType: 'blob' }),
  getBudgetKpis: (params, signal) => api.get('/budget/kpis', { params, signal }),
  deleteBudgetLignes: (annee) => api.delete('/budget/lignes', { params: { annee } }),
  getBudgetComparison: (params, signal) => api.get('/budget/comparison', { params, signal }),

  // ─── Jours ouvrés ─────────────────────────────────────────────────────
  getJoursOuvres: (params) => api.get('/budget/jours-ouvres', { params }),
  getJoursOuvresDimensions: () => api.get('/budget/jours-ouvres/dimensions'),
  getJoursOuvresCount: (params) => api.get('/budget/jours-ouvres/count', { params }),
  updateJourOuvre: (id, est_ouvre) => api.put(`/budget/jours-ouvres/${id}`, { est_ouvre }),
  importJoursOuvres: (formData) => api.post('/budget/jours-ouvres/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export default reportingFinancierApi;
